/**
 * /household — household self-management for a family's lead (the
 * earliest-created member of the family, `isFamilyOwner`). Rename the
 * household, set its timezone, rotate the family invite code, remove
 * members, and invite new members by email.
 *
 * This is deliberately separate from /admin: /admin is the single
 * app-owner (Jared) surface for instance + circle administration, while
 * every household lead — including circle-invited families — manages
 * their OWN household here. The backend scopes all /family/* writes to
 * the caller's family via familyCreatorId, so this page only ever
 * touches the signed-in lead's own household.
 */
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ScreenHeader } from '../components/ScreenHeader';
import { Section } from '../components/Section';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { IconButton } from '../components/IconButton';
import { Button } from '../components/Button';
import { IconMail, IconRefresh, IconTrash, IconXCircle } from '../components/icons';
import { TextField } from '../components/TextField';

export default function HouseholdPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  if (auth.state !== 'signed-in') return null;
  // Hard gate: only a household's lead manages it. The API also enforces
  // familyCreatorId on every /family/* write, so this is belt + braces.
  if (!auth.me.isFamilyOwner) return <Navigate to="/today" replace />;
  const { me } = auth;

  return (
    <>
      <ScreenHeader title="Household" subtitle="Manage your family and its members." />
      <div className="px-5 pt-4 pb-6">
        <HouseholdSection me={me} qc={qc} />
      </div>
    </>
  );
}

type Me = Extract<ReturnType<typeof useAuth>, { state: 'signed-in' }>['me'];

function HouseholdSection({ me, qc }: { me: Me; qc: ReturnType<typeof useQueryClient> }) {
  const [name, setName] = useState(me.family.name);
  const [tz, setTz] = useState(me.family.timezone);

  const patchFamily = useMutation({
    mutationFn: (body: { name?: string; timezone?: string }) =>
      api.patch('/family', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me'] }),
  });
  const rotate = useMutation({
    mutationFn: () =>
      api.post<{ ok: true; inviteCode: string }>('/family/rotate-invite-code'),
    onSuccess: () => {
      setRotateOpen(false);
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });
  const [rotateOpen, setRotateOpen] = useState(false);

  // Lead is me; everyone else is removable.
  const others = me.members.filter((m) => m.id !== me.member.id);

  return (
    <Section title="Household">
      <Row label="Family name">
        <TextField
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            if (name.trim() && name !== me.family.name) {
              patchFamily.mutate({ name: name.trim() });
            } else if (!name.trim()) {
              setName(me.family.name);
            }
          }}
        />
      </Row>
      <Row label="Time zone">
        <TextField
          type="text"
          mono
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          onBlur={() => {
            if (tz.trim() && tz !== me.family.timezone) {
              patchFamily.mutate({ timezone: tz.trim() });
            } else if (!tz.trim()) {
              setTz(me.family.timezone);
            }
          }}
          placeholder="America/Chicago"
        />
      </Row>
      <Row label="Invite code">
        <div className="flex items-center gap-3">
          <code className="font-mono text-oxblood text-lg tracking-wider">
            {me.family.inviteCode}
          </code>
          <IconButton
            icon={<IconRefresh />}
            label="Rotate invite code"
            busy={rotate.isPending}
            onClick={() => setRotateOpen(true)}
          />
        </div>
      </Row>

      {rotateOpen && (
        <ConfirmDialog
          title="Rotate invite code?"
          message="Any link you've already sent will stop working. People you've already invited will need to be re-invited."
          confirmLabel="Rotate code"
          busy={rotate.isPending}
          error={rotate.isError ? 'Could not rotate the code. Try again.' : null}
          onConfirm={() => rotate.mutate()}
          onCancel={() => setRotateOpen(false)}
        />
      )}

      <div className="mt-4">
        <div className="text-soft text-sm mb-2">Members</div>
        <ul className="space-y-2">
          <li className="flex items-center justify-between py-2 border-b border-line">
            <span className="text-ink">
              {me.member.displayName} <span className="text-soft text-xs">· lead</span>
            </span>
          </li>
          {others.map((m) => (
            <MemberRow key={m.id} member={m} qc={qc} />
          ))}
          {others.length === 0 && (
            <li className="text-soft text-sm italic py-2">
              Just you for now. Invite by email below to add the rest of the family.
            </li>
          )}
        </ul>
      </div>

      <InviteMemberForm />
      <PendingInvitationsList />
    </Section>
  );
}

type Member = Me['members'][number];

/**
 * One non-lead member: remove (typed-name confirm) or send an email
 * sign-in link. Both gates live in-app — `window.prompt`/`confirm` are
 * no-ops in installed iOS PWAs, which is why removal "didn't work."
 */
function MemberRow({
  member,
  qc,
}: {
  member: Member;
  qc: ReturnType<typeof useQueryClient>;
}) {
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const remove = useMutation({
    mutationFn: () => api.delete(`/family/members/${member.id}`),
    onSuccess: () => {
      setConfirmRemove(false);
      void qc.invalidateQueries({ queryKey: ['me'] });
    },
  });

  return (
    <li className="py-2 border-b border-line last:border-b-0">
      <div className="flex items-center justify-between">
        <span className="text-ink">
          {member.displayName}{' '}
          <span className="text-soft text-xs">
            · {member.role === 'parent' ? 'parent' : 'youth'}
          </span>
        </span>
        <div className="flex items-center gap-1">
          <IconButton
            icon={<IconMail />}
            label="Email sign-in link"
            onClick={() => setLinkOpen((v) => !v)}
          />
          <IconButton
            icon={<IconTrash />}
            label={`Remove ${member.displayName}`}
            variant="destructive"
            onClick={() => setConfirmRemove(true)}
          />
        </div>
      </div>

      {linkOpen && (
        <SendEmailLinkForm member={member} qc={qc} onClose={() => setLinkOpen(false)} />
      )}

      {confirmRemove && (
        <ConfirmDialog
          title={`Remove ${member.displayName}?`}
          message={
            <>
              This deletes their journal, prayers, photos, comments, and push
              subscriptions. It cannot be undone.
              <br />
              <br />
              Type <span className="text-ink font-medium">{member.displayName}</span> to
              confirm.
            </>
          }
          confirmPhrase={member.displayName}
          confirmLabel="Remove member"
          destructive
          busy={remove.isPending}
          error={
            remove.isError
              ? remove.error instanceof ApiError
                ? remove.error.message
                : 'Could not remove this member. Try again.'
              : null
          }
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmRemove(false)}
        />
      )}
    </li>
  );
}

/** Inline form to attach an email to an existing member and email them
 *  a magic sign-in link — no remove-and-re-invite required. */
function SendEmailLinkForm({
  member,
  qc,
  onClose,
}: {
  member: Member;
  qc: ReturnType<typeof useQueryClient>;
  onClose: () => void;
}) {
  const [email, setEmail] = useState('');
  const send = useMutation({
    mutationFn: () =>
      api.post(`/family/members/${member.id}/send-email-link`, { email: email.trim() }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['family-invitations'] }),
  });

  if (send.isSuccess) {
    return (
      <div className="mt-2 rounded-lg bg-line/40 px-3 py-2 text-sm">
        <span className="text-ink">Sign-in link sent to {email}.</span>{' '}
        <span className="text-soft">It expires in 7 days.</span>{' '}
        <button onClick={onClose} className="text-soft underline">
          Close
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (email.trim()) send.mutate();
      }}
      className="mt-2 flex flex-wrap items-center gap-2"
    >
      <TextField
        type="email"
        inputMode="email"
        autoComplete="off"
        placeholder={`${member.displayName}'s email`}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        maxLength={254}
        className="flex-1 min-w-[12rem] text-sm"
      />
      <Button
        type="submit"
        size="sm"
        disabled={email.trim().length === 0}
        busy={send.isPending}
      >
        Send link
      </Button>
      <Button variant="ghost" size="sm" onClick={onClose}>
        Cancel
      </Button>
      {send.isError && (
        <p className="w-full text-oxblood text-sm">
          {send.error instanceof ApiError
            ? send.error.message
            : 'Could not send the link.'}
        </p>
      )}
    </form>
  );
}

interface PendingInvitation {
  id: string;
  displayName: string;
  email: string;
  role: 'parent' | 'youth';
  /** Non-null ⇒ a sign-in link re-sent to an existing member. */
  memberId: string | null;
  createdAt: number;
  expiresAt: number;
  acceptedAt: number | null;
}

function InviteMemberForm() {
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'parent' | 'youth'>('youth');
  const [err, setErr] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      api.post('/family/invite', {
        displayName: displayName.trim(),
        email: email.trim(),
        role,
      }),
    onSuccess: () => {
      setDisplayName('');
      setEmail('');
      setRole('youth');
      setErr(null);
      void qc.invalidateQueries({ queryKey: ['family-invitations'] });
    },
    onError: (e: unknown) => {
      setErr(e instanceof ApiError ? e.message : 'Could not send invitation.');
    },
  });

  const canSubmit =
    displayName.trim().length > 0 && email.trim().length > 0 && !invite.isPending;

  return (
    <div className="mt-6">
      <div className="text-soft text-sm mb-2">Invite by email</div>
      <div className="space-y-2">
        <TextField
          type="text"
          placeholder="Their name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={40}
        />
        <TextField
          type="email"
          placeholder="email@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          maxLength={254}
        />
        <div className="flex items-center gap-3">
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as 'parent' | 'youth')}
            className="h-10 px-3 rounded-lg border border-line bg-card text-ink"
          >
            <option value="parent">Parent</option>
            <option value="youth">Youth</option>
          </select>
          <Button
            size="sm"
            onClick={() => {
              setErr(null);
              invite.mutate();
            }}
            disabled={!canSubmit}
            busy={invite.isPending}
            className="ml-auto"
          >
            Send invite
          </Button>
        </div>
        {err && <div className="text-oxblood text-sm">{err}</div>}
        {invite.isSuccess && (
          <div className="text-soft text-sm">
            Invitation sent. The link expires in 7 days.
          </div>
        )}
      </div>
    </div>
  );
}

function PendingInvitationsList() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['family-invitations'],
    queryFn: () => api.get<{ invitations: PendingInvitation[] }>('/family/invitations'),
    staleTime: 30_000,
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.delete(`/family/invitations/${id}`),
    onSuccess: () => {
      setPendingRevoke(null);
      void qc.invalidateQueries({ queryKey: ['family-invitations'] });
    },
  });
  // One dialog for the whole list, keyed by the invitation awaiting
  // confirmation — not a ConfirmDialog per row.
  const [pendingRevoke, setPendingRevoke] = useState<PendingInvitation | null>(null);

  if (!q.data || q.data.invitations.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="text-soft text-sm mb-2">Pending invitations</div>
      <ul className="space-y-2">
        {q.data.invitations.map((inv) => (
          <li
            key={inv.id}
            className="flex items-center justify-between py-2 border-b border-line last:border-b-0"
          >
            <div>
              <div className="text-ink">
                {inv.displayName}{' '}
                {inv.memberId && (
                  <span className="text-soft text-xs">· sign-in link</span>
                )}
              </div>
              <div className="text-soft text-xs">
                {inv.email} · {inv.role}
              </div>
            </div>
            <IconButton
              icon={<IconXCircle />}
              label={`Revoke invitation for ${inv.displayName}`}
              variant="destructive"
              busy={revoke.isPending && pendingRevoke?.id === inv.id}
              onClick={() => setPendingRevoke(inv)}
            />
          </li>
        ))}
      </ul>

      {pendingRevoke && (
        <ConfirmDialog
          title={`Revoke ${pendingRevoke.displayName}'s invitation?`}
          message="The link in their inbox will stop working."
          confirmLabel="Revoke"
          destructive
          busy={revoke.isPending}
          error={revoke.isError ? 'Could not revoke. Try again.' : null}
          onConfirm={() => revoke.mutate(pendingRevoke.id)}
          onCancel={() => setPendingRevoke(null)}
        />
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-soft text-sm">{label}</span>
      {children}
    </div>
  );
}
